import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import Sidebar from '@/components/Sidebar';
import Pagination from '@/components/Pagination';
import { useTranslation } from '@/hooks/useTranslation';
import { format } from 'date-fns';
import { id as idLocale } from 'date-fns/locale';
import { FaWhatsapp, FaInstagram, FaFacebookMessenger } from 'react-icons/fa';
import {
  Package,
  Search,
  Eye,
  Truck,
  CheckCircle,
  XCircle,
  Clock,
  CreditCard,
  Menu,
  X,
  Phone,
  MapPin,
  Calendar as CalendarIcon,
  DollarSign,
  QrCode,
  FileText,
  RefreshCw,
  TrendingUp,
  ShoppingBag,
  Download,
  Mail,
  Send,
  Loader2,
  Copy,
  MessageCircle,
  Instagram,
  Facebook,
  Globe,
  SlidersHorizontal,
  Inbox,
  Check,
  ChevronDown,
  AlertCircle,
  ImageIcon
} from 'lucide-react';

const STATUS_CONFIG = {
  'Pending': { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: Clock, label: 'Belum Bayar' },
  'Menunggu Pembayaran': { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: Clock, label: 'Belum Bayar' },
  'Verifikasi': { color: 'bg-orange-100 text-orange-800 border-orange-200', icon: AlertCircle, label: 'Verifikasi' },
  'Paid': { color: 'bg-indigo-100 text-blue-800 border-indigo-200', icon: CreditCard, label: 'Sudah Bayar' },
  'Sudah Bayar': { color: 'bg-indigo-100 text-blue-800 border-indigo-200', icon: CreditCard, label: 'Sudah Bayar' },
  'Shipped': { color: 'bg-purple-100 text-purple-800 border-purple-200', icon: Truck, label: 'Dikirim' },
  'Dikirim': { color: 'bg-purple-100 text-purple-800 border-purple-200', icon: Truck, label: 'Dikirim' },
  'Completed': { color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle, label: 'Selesai' },
  'Selesai': { color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle, label: 'Selesai' },
  'Cancelled': { color: 'bg-red-100 text-red-800 border-red-200', icon: XCircle, label: 'Dibatalkan' },
  'Dibatalkan': { color: 'bg-red-100 text-red-800 border-red-200', icon: XCircle, label: 'Dibatalkan' }
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0
  }).format(amount || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('id-ID', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Jakarta'
  });
};

// Channel config for mobile cards
const CHANNEL_CONFIG = {
  'whatsapp': { icon: FaWhatsapp, bg: 'bg-green-500', color: 'text-white' },
  'instagram': { icon: FaInstagram, bg: 'bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400', color: 'text-white' },
  'messenger': { icon: FaFacebookMessenger, bg: 'bg-indigo-500', color: 'text-white' },
  'website': { icon: Globe, bg: 'bg-slate-500', color: 'text-white' }
};

// Status pesanan tersimpan campur Inggris & Indonesia ("Sudah Bayar" vs "Paid"),
// sedangkan dropdown memakai nilai Inggris. Tanpa pemetaan ini dropdown salah
// menampilkan "Belum Bayar" untuk pesanan yang sudah lunas.
const STATUS_SELECT_VALUE = {
  'Menunggu Pembayaran': 'Pending', 'Pending': 'Pending',
  'Verifikasi': 'Verifikasi',
  'Sudah Bayar': 'Paid', 'Paid': 'Paid',
  'Dikirim': 'Shipped', 'Shipped': 'Shipped',
  'Selesai': 'Completed', 'Completed': 'Completed',
  'Dibatalkan': 'Cancelled', 'Cancelled': 'Cancelled',
};
const toSelectValue = (status) => STATUS_SELECT_VALUE[status] || 'Pending';

// Order Detail Modal
const OrderDetailModal = ({ order, isOpen, onClose, onStatusUpdate, onTrackingUpdate, onNavigateChat }) => {
  const { user, token } = useAuth();
  const [newStatus, setNewStatus] = useState(toSelectValue(order?.status));
  // Order produk digital tidak punya pengiriman: field ongkir/alamat/resi tidak relevan.
  const needsShipping = order?.requires_shipping !== false;
  const [trackingNumber, setTrackingNumber] = useState(order?.tracking_number || '');
  const [courier, setCourier] = useState(order?.courier || '');
  const [notes, setNotes] = useState(order?.notes || '');
  const [saving, setSaving] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [paymentMode, setPaymentMode] = useState('manual');
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [qrisName, setQrisName] = useState('');
  
  // Auto Booking & Pickup states
  const [shippingMode, setShippingMode] = useState('manual'); // 'manual' | 'auto'
  const [deliveryMethod, setDeliveryMethod] = useState('pickup'); // 'pickup' | 'dropoff'
  const [bookingLoading, setBookingLoading] = useState(false);
  const [pickupLoading, setPickupLoading] = useState(false);
  const [labelLoading, setLabelLoading] = useState(false);
  const [pickupDate, setPickupDate] = useState('');
  const [pickupTime, setPickupTime] = useState('10:00');
  const [pickupVehicle, setPickupVehicle] = useState('Motor');
  const [shippingBooking, setShippingBooking] = useState(null);
  const [showPaymentProof, setShowPaymentProof] = useState(false);
  const [deliveries, setDeliveries] = useState([]);
  const [delivering, setDelivering] = useState(false);
  const [resendingInvoice, setResendingInvoice] = useState(false);

  // Collapsible section states
  const [expandedSections, setExpandedSections] = useState({
    customer: false,
    payment: false,
    products: false,
    shipping: true,
    invoice: false,
  });
  const toggleSection = (key) => setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (order) {
      setNewStatus(toSelectValue(order.status));
      setTrackingNumber(order.tracking_number || '');
      setCourier(order.courier || '');
      setNotes(order.notes || '');
      fetchInvoice();
      fetchPaymentMode();
      fetchPaymentMethods();
      fetchQrisSettings();
      fetchShippingBooking();
      if (order.requires_shipping === false || order.order_kind === 'digital' || order.order_kind === 'mixed') {
        fetchDeliveries();
      }
      
      // Set default pickup date to today
      const today = new Date();
      setPickupDate(today.toISOString().split('T')[0]);
      
      // Check if order has auto booking
      if (order.komerce_order_no) {
        setShippingMode('auto');
      }
    }
  }, [order]);

  const fetchPaymentMode = async () => {
    try {
      const response = await axios.get(`${API}/payment/mode?user_id=${user?.id}`, { headers });
      setPaymentMode(response.data.mode || 'manual');
    } catch (error) {
      console.error('Error fetching payment mode:', error);
    }
  };

  const fetchDeliveries = async () => {
    try {
      const res = await axios.get(`${API}/digital/deliveries?order_id=${order.id}`, { headers });
      setDeliveries(res.data.deliveries || []);
    } catch (error) {
      console.error('Error fetching digital deliveries:', error);
    }
  };

  const handleDeliverDigital = async () => {
    setDelivering(true);
    try {
      const res = await axios.post(`${API}/digital/deliver/${order.id}`, {}, { headers });
      const r = res.data.result || {};
      if (r.failures?.length) {
        toast.error(`Sebagian gagal: ${r.failures.map(f => f.product).join(', ')}`);
      } else {
        toast.success('Produk digital dikirim ke customer');
      }
      fetchDeliveries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal mengirim produk digital');
    } finally {
      setDelivering(false);
    }
  };

  const handleResendInvoice = async () => {
    setResendingInvoice(true);
    try {
      const res = await axios.post(`${API}/orders/${order.id}/resend-invoice`, {}, { headers });
      toast.success(`Invoice dikirim ulang ke ${res.data.sent_to}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal mengirim ulang invoice');
    } finally {
      setResendingInvoice(false);
    }
  };

  // Nama QRIS diambil dari Pengaturan > Pembayaran (bukan dari data order), supaya
  // order lama yang sempat menyimpan nama pemilik rekening bank tidak ikut tampil.
  const fetchQrisSettings = async () => {
    if (!user?.id) return;
    try {
      const response = await axios.get(`${API}/payment/qris?user_id=${user?.id}`, { headers });
      setQrisName(response.data?.qris_name || '');
    } catch (error) {
      console.error('Error fetching QRIS settings:', error);
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const response = await axios.get(`${API}/payment-methods?user_id=${user?.id}`, { headers });
      setPaymentMethods(response.data.methods || []);
    } catch (error) {
      console.error('Error fetching payment methods:', error);
    }
  };
  
  const fetchShippingBooking = async () => {
    if (!order?.komerce_order_no) return;
    try {
      const response = await axios.get(`${API}/shipping/track/${order.komerce_order_no}`, { headers });
      if (response.data.success) {
        setShippingBooking(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching shipping booking:', error);
    }
  };

  // Auto Booking - Create shipment and get order_no
  const handleAutoBooking = async () => {
    setBookingLoading(true);
    try {
      let destinationId = order?.shipping_destination_id;
      
      // If no destination_id, resolve it from address
      if (!destinationId) {
        const resolveRes = await axios.post(
          `${API}/shipping/resolve-destination`,
          { order_id: order.id, address: order.address },
          { headers }
        );
        
        if (resolveRes.data.success && resolveRes.data.destination_id) {
          destinationId = resolveRes.data.destination_id;
        } else {
          toast.error('Gagal menentukan destinasi pengiriman dari alamat customer');
          setBookingLoading(false);
          return;
        }
      }
      
      const bookingData = {
        order_id: order.id,
        courier: (order.courier || courier || 'JNE').split(' ')[0],
        service: order.courier_service || 'REG',
        receiver_name: order.customer_name || order.customer?.name || 'Customer',
        receiver_phone: order.customer_phone || order.customer?.phone || '081234567890',
        receiver_address: order.address || order.shipping_address || 'Alamat',
        receiver_destination_id: destinationId,
        products: order.items?.map(item => ({
          name: item.name || item.product_name || item.product || 'Produk',
          variant: item.variant || '-',
          price: item.price || item.unit_price || item.subtotal || 0,
          weight: item.weight || 500,
          qty: item.quantity || item.qty || 1,
          width: 10,
          height: 10,
          length: 10
        })) || [{
          name: order.product || 'Produk',
          variant: order.variant || '-',
          price: order.unit_price || order.subtotal || 0,
          weight: 500,
          qty: 1,
          width: 10,
          height: 10,
          length: 10
        }],
        shipping_cost: order.shipping_cost || 0,
        grand_total: order.total || order.grand_total || 0
      };
      
      const response = await axios.post(
        `${API}/shipping/book?user_id=${user?.id}`,
        bookingData,
        { headers }
      );
      
      if (response.data.success) {
        toast.success('Booking berhasil! Silakan request pickup.');
        setShippingBooking(response.data.data);
        setShippingMode('auto');
        // Refresh order data
        onStatusUpdate && onStatusUpdate(order.id, order.status);
      }
    } catch (error) {
      console.error('Auto booking error:', error);
      toast.error(error.response?.data?.detail || 'Gagal melakukan booking');
    } finally {
      setBookingLoading(false);
    }
  };

  // Request Pickup - Get AWB/Resi
  const handleRequestPickup = async () => {
    const orderNo = order?.komerce_order_no || shippingBooking?.order_no;
    if (!orderNo) {
      toast.error('Silakan lakukan booking terlebih dahulu');
      return;
    }
    
    if (!pickupDate) {
      toast.error('Pilih tanggal pickup');
      return;
    }
    
    setPickupLoading(true);
    try {
      const response = await axios.post(
        `${API}/shipping/pickup?user_id=${user?.id}`,
        {
          order_no: orderNo,
          pickup_date: pickupDate,
          pickup_time: pickupTime,
          pickup_vehicle: pickupVehicle
        },
        { headers }
      );
      
      if (response.data.success) {
        const results = response.data.data.results || [];
        const successResult = results.find(r => r.status === 'success');
        
        if (successResult?.awb) {
          toast.success(`Pickup dijadwalkan! Resi: ${successResult.awb}`);
          setTrackingNumber(successResult.awb);
          // Update order tracking
          await onTrackingUpdate(order.id, successResult.awb, courier);
        } else {
          toast.success('Pickup berhasil dijadwalkan');
        }
        
        fetchShippingBooking();
      }
    } catch (error) {
      console.error('Pickup request error:', error);
      toast.error(error.response?.data?.detail || 'Gagal request pickup');
    } finally {
      setPickupLoading(false);
    }
  };

  // Print Label
  const handlePrintLabel = async () => {
    const orderNo = order?.komerce_order_no || shippingBooking?.order_no;
    if (!orderNo) {
      toast.error('Order belum di-booking');
      return;
    }
    
    setLabelLoading(true);
    try {
      const response = await axios.post(
        `${API}/shipping/label`,
        {
          order_no: orderNo,
          page_format: 'page_5' // Thermal 10x10
        },
        { headers }
      );
      
      if (response.data.success) {
        const { base64, download_url } = response.data.data;
        
        if (base64) {
          // Open base64 PDF in new tab
          const pdfWindow = window.open('');
          pdfWindow.document.write(
            `<iframe width='100%' height='100%' src='data:application/pdf;base64,${base64}'></iframe>`
          );
        } else if (download_url) {
          window.open(download_url, '_blank');
        }
        
        toast.success('Label berhasil di-generate');
      }
    } catch (error) {
      console.error('Print label error:', error);
      toast.error(error.response?.data?.detail || 'Gagal generate label');
    } finally {
      setLabelLoading(false);
    }
  };

  const fetchInvoice = async () => {
    if (!order?.id) return;
    setLoadingInvoice(true);
    try {
      const response = await axios.get(`${API}/invoices/by-order/${order.id}`, { headers });
      setInvoice(response.data.invoice);
    } catch (error) {
      console.error('Error fetching invoice:', error);
    } finally {
      setLoadingInvoice(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!invoice?.id) return;
    setDownloadingPdf(true);
    try {
      const response = await axios.get(`${API}/invoices/${invoice.id}/pdf`, {
        headers,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice-${invoice.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Invoice berhasil didownload');
    } catch (error) {
      toast.error('Gagal download invoice');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleSendEmail = async () => {
    if (!invoice?.id) return;
    if (!invoice?.customer_email) {
      toast.error('Email customer tidak tersedia');
      return;
    }
    setSendingEmail(true);
    try {
      await axios.post(`${API}/invoices/${invoice.id}/send-email`, {}, { headers });
      toast.success(`Invoice dikirim ke ${invoice.customer_email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal mengirim email');
    } finally {
      setSendingEmail(false);
    }
  };

  if (!order) return null;

  const StatusIcon = STATUS_CONFIG[order.status]?.icon || Clock;

  const handleStatusUpdate = async () => {
    setSaving(true);
    await onStatusUpdate(order.id, newStatus);
    setSaving(false);
  };

  const handleTrackingUpdate = async () => {
    if (!trackingNumber.trim()) {
      toast.error('Masukkan nomor resi');
      return;
    }
    setSaving(true);
    await onTrackingUpdate(order.id, trackingNumber, courier);
    setSaving(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent hideClose className="sm:max-w-2xl max-h-[85vh] p-0 gap-0 overflow-hidden bg-white dark:bg-[#0A0A0A] rounded-t-2xl sm:rounded-xl w-[95vw] sm:w-full" data-testid="order-detail-modal">
        {/* Drag Handle (mobile) */}
        <div className="flex justify-center pt-2 pb-0 sm:hidden">
          <div className="w-10 h-1 rounded-full bg-slate-300 dark:bg-zinc-700" />
        </div>

        {/* Sticky Header */}
        <div className="sticky top-0 z-10 bg-white dark:bg-[#0A0A0A] px-4 pt-3 pb-3 border-b border-slate-100 dark:border-zinc-800/60">
          <div className="flex items-center justify-between">
            <DialogHeader className="p-0 flex-1">
              <DialogTitle className="flex items-center gap-2 text-base font-semibold">
                <Package className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                Detail Pesanan
              </DialogTitle>
            </DialogHeader>
            <button onClick={() => onClose(false)} className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-slate-100 dark:hover:bg-zinc-900 transition-colors shrink-0 dark:bg-zinc-800" data-testid="close-order-detail-btn" aria-label="Close">
              <XCircle className="w-5 h-5 text-slate-500 dark:text-zinc-400" />
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto flex-1 px-4 pb-4 sm:pb-16" style={{ maxHeight: 'calc(85vh - 130px)' }}>
          <div className="space-y-3 pt-4">

            {/* Pelanggan minta batal lewat chat, tapi pesanan sudah dibayar/dikirim */}
            {order.cancel_requested_at && !['Cancelled', 'Dibatalkan'].includes(order.status) && (
              <div
                className="rounded-xl border border-amber-300 bg-amber-50 dark:border-amber-800/60 dark:bg-amber-950/40 px-4 py-3"
                data-testid="cancel-request-alert"
              >
                <p className="text-sm font-semibold text-amber-900 dark:text-amber-300">
                  Pelanggan minta pembatalan
                </p>
                <p className="text-xs text-amber-800 dark:text-amber-400/90 mt-1">
                  Diminta {new Date(order.cancel_requested_at).toLocaleString('id-ID', { timeZone: 'Asia/Jakarta' })} WIB. Pesanan tidak
                  dibatalkan otomatis karena statusnya {STATUS_CONFIG[order.status]?.label || 'sudah diproses'}.
                  Ubah status ke Dibatalkan bila disetujui, stok akan dikembalikan otomatis.
                </p>
              </div>
            )}

            {/* ═══ SUMMARY HERO ═══ */}
            <div className="rounded-xl border border-slate-100 dark:border-zinc-800/60 overflow-hidden" data-testid="order-summary-hero">
              {/* Status + ID Row */}
              <div className="flex items-center justify-between px-4 pt-4 pb-2">
                <Badge className={`${STATUS_CONFIG[order.status]?.color} px-3 py-1.5 text-xs font-bold border`} data-testid="order-status-badge">
                  <StatusIcon className="w-3.5 h-3.5 mr-1" />
                  {STATUS_CONFIG[order.status]?.label}
                </Badge>
                <span className="text-[10px] font-mono text-slate-500 dark:text-zinc-500 truncate max-w-[120px]">{order.id}</span>
              </div>

              {/* Product Summary */}
              <div className="px-4 pb-2">
                {order.items?.length > 0 ? (
                  <div>
                    <p className="text-sm font-semibold text-slate-900 dark:text-zinc-50">
                      {order.items[0].product || '-'}
                      {order.items.length > 1 && <span className="text-slate-500 font-normal text-xs dark:text-zinc-400"> +{order.items.length - 1} lainnya</span>}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-zinc-400 mt-0.5">
                      {order.items.map(i => `${i.variant || i.product || ''} x${i.quantity || 1}`).filter(s => s.trim()).join(', ')}
                    </p>
                  </div>
                ) : (
                  <div>
                    <p className="text-sm font-semibold text-slate-900 dark:text-zinc-50">{order.product || '-'}</p>
                    {order.variant && <p className="text-xs text-slate-500 mt-0.5 dark:text-zinc-400">{order.variant} · Qty: {order.quantity || 1}</p>}
                  </div>
                )}
              </div>

              {/* Total Transfer - HERO */}
              <div className="mx-4 mb-4 bg-slate-50 dark:bg-zinc-900 rounded-xl p-3 text-center">
                <p className="text-[10px] uppercase tracking-widest text-slate-500 dark:text-zinc-500 mb-0.5">
                  {order.unique_code > 0 ? 'Total Transfer' : 'Total'}
                </p>
                <p className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50 tabular-nums" data-testid="order-total-hero" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {formatCurrency(order.unique_code > 0 ? order.transfer_total : order.total)}
                </p>
                {order.unique_code > 0 && (
                  <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-0.5">
                    termasuk kode unik +{formatCurrency(order.unique_code)}
                  </p>
                )}
              </div>
            </div>

            {/* ═══ PAYMENT PROOF + VERIFY ACTION ═══ */}
            {order.status === 'Verifikasi' && (
              <div className="rounded-xl border-2 border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/20 p-4" data-testid="payment-proof-section">
                <div className="flex items-center gap-2 mb-3">
                  <AlertCircle className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                  <span className="text-sm font-semibold text-orange-800 dark:text-orange-300">Menunggu Verifikasi Pembayaran</span>
                </div>
                {order.payment_proof_url && (
                  <Button
                    variant="outline"
                    className="w-full mb-2 gap-2 border-orange-300 text-orange-700 hover:bg-orange-100 dark:border-orange-700 dark:text-orange-400 dark:bg-orange-950/40"
                    onClick={() => setShowPaymentProof(true)}
                    data-testid="view-payment-proof-btn"
                  >
                    <ImageIcon className="w-4 h-4" /> Lihat Bukti Pembayaran
                  </Button>
                )}
                <Button
                  className="w-full gap-2 bg-green-600 hover:bg-green-700 text-white"
                  onClick={async () => {
                    try {
                      setSaving(true);
                      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/orders/${order.id}/status`, 
                        { status: 'Sudah Bayar' }, { headers });
                      toast.success('Pembayaran dikonfirmasi!');
                      onStatusUpdate(order.id, 'Sudah Bayar');
                      onClose(false);
                    } catch (e) {
                      toast.error('Gagal update status');
                    } finally {
                      setSaving(false);
                    }
                  }}
                  disabled={saving}
                  data-testid="confirm-payment-btn"
                >
                  <Check className="w-4 h-4" /> Tandai Lunas
                </Button>
              </div>
            )}

            {/* Payment proof for already paid orders */}
            {order.payment_proof_url && order.status !== 'Verifikasi' && (
              <Button
                variant="outline"
                size="sm"
                className="w-full gap-2 text-xs"
                onClick={() => setShowPaymentProof(true)}
                data-testid="view-payment-proof-paid-btn"
              >
                <ImageIcon className="w-3.5 h-3.5" /> Lihat Bukti Pembayaran
              </Button>
            )}

            {/* Payment Proof Image Modal */}
            <Dialog open={showPaymentProof} onOpenChange={setShowPaymentProof}>
              <DialogContent className="max-w-[90vw] sm:max-w-sm p-0 overflow-hidden">
                <div className="p-3 border-b border-slate-200 dark:border-zinc-800">
                  <h3 className="font-semibold text-sm text-slate-900 dark:text-zinc-50">Bukti Pembayaran</h3>
                  <p className="text-xs text-slate-500 mt-0.5 dark:text-zinc-400">{order.customer_name} - {formatCurrency(order.total)}</p>
                </div>
                <div className="p-3">
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/image-proxy?url=${encodeURIComponent(order.payment_proof_url || '')}`}
                    alt="Bukti Pembayaran"
                    className="w-full max-h-[65vh] object-contain rounded-lg"
                    onError={(e) => { e.target.src = order.payment_proof_url; }}
                  />
                </div>
              </DialogContent>
            </Dialog>

            {/* ═══ Customer + Payment: side-by-side on desktop ═══ */}
            <div className="sm:grid sm:grid-cols-2 sm:gap-3 space-y-3 sm:space-y-0">
              {/* Customer Info */}
              <div className="rounded-xl border border-slate-100 dark:border-zinc-800/60 overflow-hidden">
                <button onClick={() => toggleSection('customer')} className="w-full flex items-center justify-between px-4 py-3 bg-white dark:bg-zinc-900 hover:bg-slate-50 dark:hover:bg-zinc-800/50 transition-colors sm:pointer-events-none" data-testid="toggle-customer-section">
                  <span className="flex items-center gap-2 text-xs font-semibold text-slate-600 dark:text-zinc-300 uppercase tracking-wide">
                    <Phone className="w-3.5 h-3.5" /> Info Customer
                  </span>
                  <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform duration-200 sm:hidden ${expandedSections.customer ? 'rotate-180' : ''} dark:text-zinc-400`} />
                </button>
                <div className={`px-4 pb-3 space-y-2 bg-white dark:bg-zinc-900 ${expandedSections.customer ? '' : 'hidden sm:block'}`}>
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <p className="text-slate-500 dark:text-zinc-500">Nama</p>
                      <p className="font-medium text-slate-900 dark:text-zinc-50 mt-0.5">{order.customer_name || '-'}</p>
                    </div>
                    <div>
                      <p className="text-slate-500 dark:text-zinc-500">No HP</p>
                      <p className="font-medium text-slate-900 dark:text-zinc-50 mt-0.5">{order.customer_phone || '-'}</p>
                    </div>
                  </div>
                  {order.address && (
                    <div className="text-xs">
                      <p className="text-slate-500 dark:text-zinc-500">Alamat</p>
                      <p className="font-medium text-slate-900 dark:text-zinc-50 mt-0.5 leading-relaxed">{order.address}</p>
                    </div>
                  )}
                  {!needsShipping && order.customer_email && (
                    <div className="text-xs" data-testid="order-digital-email">
                      <p className="text-slate-500 dark:text-zinc-500">Email tujuan produk digital</p>
                      <p className="font-medium text-slate-900 dark:text-zinc-50 mt-0.5 break-all">{order.customer_email}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Payment Method */}
              <div className="rounded-xl border border-slate-100 dark:border-zinc-800/60 overflow-hidden">
                <button onClick={() => toggleSection('payment')} className="w-full flex items-center justify-between px-4 py-3 bg-white dark:bg-zinc-900 hover:bg-slate-50 dark:hover:bg-zinc-800/50 transition-colors sm:pointer-events-none" data-testid="toggle-payment-section">
                  <span className="flex items-center gap-2 text-xs font-semibold text-slate-600 dark:text-zinc-300 uppercase tracking-wide">
                    <CreditCard className="w-3.5 h-3.5" /> Pembayaran
                  </span>
                  <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform duration-200 sm:hidden ${expandedSections.payment ? 'rotate-180' : ''} dark:text-zinc-400`} />
                </button>
                <div className={`px-4 pb-3 space-y-2 bg-white dark:bg-zinc-900 ${expandedSections.payment ? '' : 'hidden sm:block'}`}>
                  {order.payment_method ? (() => {
                    const upper = (order.payment_method || '').toUpperCase();
                    const isQris = upper.includes('QRIS');
                    const isCod = upper.includes('COD');
                    // QRIS/COD tidak punya nomor rekening — jangan pernah menampilkan
                    // rekening bank di bawahnya (bug: order QRIS memunculkan rekening BCA).
                    const matchedPm = paymentMethods.find(pm => {
                      const pmName = (pm.name || '').toLowerCase();
                      const methodName = upper.toLowerCase();
                      return pm.is_active && pmName && (methodName.includes(pmName) || pmName.includes(methodName.replace('transfer bank ', '')));
                    });
                    const accountNumber = isQris || isCod ? '' : (order.payment_account_number || matchedPm?.account_number || '');
                    // QRIS: nama SELALU dari Pengaturan > Pembayaran (qris_name).
                    // Data order lama bisa berisi nama pemilik rekening bank — jangan dipakai.
                    const accountName = isQris ? qrisName : (order.payment_account_name || matchedPm?.account_name || '');
                    return (
                      <div className="flex items-center gap-2.5 p-2.5 bg-slate-50 dark:bg-zinc-800/50 rounded-lg" data-testid="order-payment-card">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${isQris ? 'bg-violet-100 dark:bg-violet-950/40' : isCod ? 'bg-emerald-100 dark:bg-emerald-950/40' : 'bg-indigo-100 dark:bg-indigo-950/40'}`}>
                          {isQris ? <QrCode className="w-4 h-4 text-violet-600 dark:text-violet-400" />
                            : isCod ? <DollarSign className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                            : <CreditCard className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-900 dark:text-zinc-50 text-xs">{order.payment_method}</p>
                          {isQris ? (
                            <p className="text-[11px] text-slate-500 truncate dark:text-zinc-400 mt-0.5" data-testid="payment-qris-name">
                              {accountName || '-'}
                            </p>
                          ) : isCod ? (
                            <p className="text-[11px] text-slate-500 dark:text-zinc-400 mt-0.5">Bayar saat barang diterima</p>
                          ) : accountNumber ? (
                            <div className="flex items-center gap-1 mt-0.5">
                              <p className="text-[11px] text-slate-500 truncate dark:text-zinc-400" data-testid="payment-account-line">
                                {accountNumber}{accountName ? ` · a.n ${accountName}` : ''}
                              </p>
                              <button onClick={() => { navigator.clipboard.writeText(accountNumber); toast.success('Disalin'); }} className="p-0.5 hover:bg-slate-200 dark:hover:bg-slate-600 rounded shrink-0" data-testid="copy-account-btn">
                                <Copy className="w-3 h-3 text-slate-500 dark:text-zinc-400" />
                              </button>
                            </div>
                          ) : (
                            <p className="text-[11px] text-slate-500 dark:text-zinc-400 mt-0.5">-</p>
                          )}
                        </div>
                      </div>
                    );
                  })() : paymentMethods.filter(pm => pm.is_active).length > 0 ? (
                    <div className="space-y-2">
                      {paymentMethods.filter(pm => pm.is_active).map((pm, idx) => (
                        <div key={idx} className="flex items-center gap-2.5 p-2.5 bg-slate-50 dark:bg-zinc-800/50 rounded-lg">
                          <div className={`w-8 h-8 ${pm.type === 'bank' ? 'bg-indigo-100 dark:bg-indigo-950/40' : 'bg-green-100 dark:bg-green-900/30'} rounded-lg flex items-center justify-center shrink-0`}>
                            {pm.type === 'bank' ? <CreditCard className="w-4 h-4 text-indigo-600 dark:text-indigo-400" /> : <DollarSign className="w-4 h-4 text-green-600 dark:text-green-400" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-slate-900 dark:text-zinc-50 text-xs">{pm.name}</p>
                            <p className="text-[11px] text-slate-500 truncate dark:text-zinc-400">{pm.account_number} · a.n {pm.account_name}</p>
                          </div>
                          <button onClick={() => { navigator.clipboard.writeText(pm.account_number); toast.success(`Nomor ${pm.name} disalin`); }} className="p-1.5 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg shrink-0">
                            <Copy className="w-3.5 h-3.5 text-slate-500 dark:text-zinc-400" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500 italic dark:text-zinc-400">Belum ada metode pembayaran yang dikonfigurasi</p>
                  )}
                </div>
              </div>
            </div>

            {/* ═══ COLLAPSIBLE: Product Details ═══ */}
            <div className="rounded-xl border border-slate-100 dark:border-zinc-800/60 overflow-hidden">
              <button onClick={() => toggleSection('products')} className="w-full flex items-center justify-between px-4 py-3 bg-white dark:bg-zinc-900 hover:bg-slate-50 dark:hover:bg-zinc-800/50 transition-colors sm:pointer-events-none" data-testid="toggle-products-section">
                <span className="flex items-center gap-2 text-xs font-semibold text-slate-600 dark:text-zinc-300 uppercase tracking-wide">
                  <ShoppingBag className="w-3.5 h-3.5" /> Detail Produk
                  {order.items?.length > 1 && <Badge className="bg-indigo-100 text-indigo-700 text-[10px] px-1.5 py-0 h-4 ml-1 dark:text-indigo-300 dark:bg-indigo-950/40">{order.items.length} item</Badge>}
                </span>
                <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform duration-200 sm:hidden ${expandedSections.products ? 'rotate-180' : ''} dark:text-zinc-400`} />
              </button>
              <div className={`px-4 pb-3 bg-white dark:bg-zinc-900 ${expandedSections.products ? '' : 'hidden sm:block'}`}>
                  {order.items && order.items.length > 0 ? (
                    <div className="space-y-2">
                      {order.items.map((item, idx) => (
                        <div key={idx} className={`text-xs ${idx > 0 ? 'pt-2 border-t border-slate-100 dark:border-zinc-800/60' : ''}`}>
                          <div className="flex justify-between items-start">
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-slate-900 dark:text-zinc-50">{item.product || '-'}</p>
                              {item.variant && item.variant !== '-' && <p className="text-slate-500 mt-0.5 dark:text-zinc-400">Varian: {item.variant}</p>}
                            </div>
                            <Badge variant="outline" className="text-[10px] ml-2 shrink-0">x{item.quantity || 1}</Badge>
                          </div>
                          <div className="flex justify-between mt-1 text-slate-500 dark:text-zinc-400">
                            <span>@ {formatCurrency(item.unit_price || 0)}</span>
                            <span className="font-medium text-slate-700 dark:text-zinc-300">{formatCurrency((item.unit_price || 0) * (item.quantity || 1))}</span>
                          </div>
                        </div>
                      ))}
                      <div className="pt-2 border-t border-slate-200 dark:border-zinc-700 space-y-1 text-xs">
                        <div className="flex justify-between"><span className="text-slate-500 dark:text-zinc-400">Subtotal</span><span className="text-slate-700 dark:text-zinc-300">{formatCurrency(order.items.reduce((sum, item) => sum + ((item.unit_price || 0) * (item.quantity || 1)), 0))}</span></div>
                        {order.shipping_cost > 0 && <div className="flex justify-between"><span className="text-slate-500 dark:text-zinc-400">Ongkir</span><span className="text-slate-700 dark:text-zinc-300">{formatCurrency(order.shipping_cost)}</span></div>}
                        {order.unique_code > 0 && <div className="flex justify-between"><span className="text-slate-500 dark:text-zinc-400">Kode Unik</span><span className="text-amber-600 dark:text-amber-400">+{formatCurrency(order.unique_code)}</span></div>}
                        <div className="flex justify-between pt-1 border-t border-slate-100 dark:border-zinc-800">
                          <span className="font-semibold text-slate-700 dark:text-zinc-300">{order.unique_code > 0 ? 'Total Transfer' : 'TOTAL'}</span>
                          <span className="font-bold text-indigo-600 dark:text-indigo-400">{formatCurrency(order.unique_code > 0 ? order.transfer_total : order.total)}</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div><p className="text-slate-500 dark:text-zinc-400">Produk</p><p className="font-medium text-slate-900 dark:text-zinc-50 mt-0.5">{order.product || '-'}</p></div>
                      <div><p className="text-slate-500 dark:text-zinc-400">Varian</p><p className="font-medium text-slate-900 dark:text-zinc-50 mt-0.5">{order.variant || '-'}</p></div>
                      <div><p className="text-slate-500 dark:text-zinc-400">Qty</p><p className="font-medium text-slate-900 dark:text-zinc-50 mt-0.5">{order.quantity || 1}</p></div>
                      <div><p className="text-slate-500 dark:text-zinc-400">Harga Satuan</p><p className="font-medium text-slate-900 dark:text-zinc-50 mt-0.5">{formatCurrency(order.unit_price)}</p></div>
                      <div className="col-span-2"><p className="text-slate-500 dark:text-zinc-400">Total</p><p className="font-bold text-indigo-600 dark:text-indigo-400 mt-0.5">{formatCurrency(order.total)}</p></div>
                    </div>
                  )}
                </div>
            </div>

            {/* ═══ Disbursement Status (inline, not collapsible) ═══ */}
            {paymentMode === 'automatic' && (order.status === 'Sudah Bayar' || order.status === 'Paid') && (
              <div className={`rounded-xl p-3 border text-xs ${
                order.disbursement_status === 'Success'
                  ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                  : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
              }`} data-testid="disbursement-status">
                {order.disbursement_status === 'Success' ? (
                  <div className="text-green-700 dark:text-green-300 space-y-0.5">
                    <p className="font-semibold flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5" /> Dana dicairkan</p>
                    {order.disbursement_amount && <p>Jumlah: {formatCurrency(order.disbursement_amount)}</p>}
                    {order.disbursed_at && <p className="text-green-600 text-[10px] dark:text-green-400">{formatDate(order.disbursed_at)}</p>}
                  </div>
                ) : (
                  <div className="text-amber-700 dark:text-amber-300 space-y-0.5">
                    <p className="font-semibold flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Dana belum dicairkan</p>
                    <p className="text-[10px]">Pencairan akan diproses sesuai jadwal.</p>
                  </div>
                )}
              </div>
            )}

            {/* ═══ COLLAPSIBLE: Shipping — hanya untuk order yang perlu dikirim ═══ */}
            {needsShipping && (
            <div className="rounded-xl border border-slate-100 dark:border-zinc-800/60 overflow-hidden">
              <button onClick={() => toggleSection('shipping')} className="w-full flex items-center justify-between px-4 py-3 bg-white dark:bg-zinc-900 hover:bg-slate-50 dark:hover:bg-zinc-800/50 transition-colors sm:pointer-events-none" data-testid="toggle-shipping-section">
                <span className="flex items-center gap-2 text-xs font-semibold text-slate-600 dark:text-zinc-300 uppercase tracking-wide">
                  <Truck className="w-3.5 h-3.5" /> Pengiriman
                </span>
                <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform duration-200 sm:hidden ${expandedSections.shipping ? 'rotate-180' : ''} dark:text-zinc-400`} />
              </button>
              <div className={`px-4 pb-3 bg-white dark:bg-zinc-900 space-y-3 ${expandedSections.shipping ? '' : 'hidden sm:block'}`}>
                  {order.estimated_ship_label && (
                    <div className="flex items-center justify-between text-xs rounded-lg bg-slate-50 dark:bg-zinc-800/60 px-2.5 py-2" data-testid="estimated-ship-row">
                      <span className="text-slate-500 dark:text-zinc-400">Estimasi kirim</span>
                      <span className="font-medium text-slate-900 dark:text-zinc-50">{order.estimated_ship_label}</span>
                    </div>
                  )}
                  {/* Mode Toggle */}
                  <div className="flex gap-2">
                    <button onClick={() => setShippingMode('manual')} className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium transition-colors ${shippingMode === 'manual' ? 'bg-indigo-100 text-indigo-700 dark:bg-blue-900/50 dark:text-indigo-300' : 'bg-slate-100 text-slate-500 dark:bg-zinc-800 dark:text-zinc-400'}`}>
                      Input Manual
                    </button>
                    <button onClick={() => setShippingMode('auto')} className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium transition-colors ${shippingMode === 'auto' ? 'bg-indigo-100 text-indigo-700 dark:bg-blue-900/50 dark:text-indigo-300' : 'bg-slate-100 text-slate-500 dark:bg-zinc-800 dark:text-zinc-400'}`}>
                      Auto Booking
                    </button>
                  </div>

                  {shippingMode === 'manual' ? (
                    <div className="space-y-2">
                      <div>
                        <Label className="text-xs">Kurir</Label>
                        <Input value={courier} onChange={(e) => setCourier(e.target.value)} placeholder="JNE, J&T, SiCepat..." className="mt-1 h-9 text-sm rounded-lg" />
                      </div>
                      <div>
                        <Label className="text-xs">Nomor Resi</Label>
                        <Input value={trackingNumber} onChange={(e) => setTrackingNumber(e.target.value)} placeholder="Masukkan nomor resi" className="mt-1 h-9 text-sm rounded-lg" />
                      </div>
                      <Button onClick={handleTrackingUpdate} disabled={saving} variant="outline" className="w-full h-9 text-xs rounded-xl gap-1.5" data-testid="save-tracking-btn">
                        <Truck className="w-3.5 h-3.5" /> Tandai Dikirim
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {/* Development Notice */}
                      <div className="flex items-start gap-2 p-2.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                        <AlertCircle className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                        <p className="text-[11px] leading-relaxed text-amber-700 dark:text-amber-300">
                          Fitur <strong>Auto Booking</strong> sedang dalam tahap pengembangan. Untuk saat ini, gunakan <button onClick={() => setShippingMode('manual')} className="underline font-semibold hover:text-amber-900 dark:hover:text-amber-200 dark:text-amber-300">Input Manual</button> untuk mengirim pesanan.
                        </p>
                      </div>

                      {/* === AUTO BOOKING CONTENT (HIDDEN - dalam pengembangan) === */}
                      {false && <>
                      {/* Current Booking Status */}
                      {(order?.komerce_order_no || shippingBooking) && (
                        <div className="bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-800 rounded-lg p-2.5 text-xs">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-indigo-700 dark:text-indigo-300">Order: {order?.komerce_order_no || shippingBooking?.order_no}</span>
                            <Badge className="bg-indigo-100 text-blue-800 text-[10px] dark:text-blue-300 dark:bg-indigo-950/40">{shippingBooking?.status || order?.shipping_status || 'Booked'}</Badge>
                          </div>
                          {(order?.tracking_number || shippingBooking?.awb) && (
                            <div className="flex items-center gap-1.5">
                              <span className="text-slate-500 dark:text-zinc-400">Resi:</span>
                              <span className="font-mono font-medium text-slate-700 dark:text-zinc-300">{order?.tracking_number || shippingBooking?.awb}</span>
                              <button onClick={() => { navigator.clipboard.writeText(order?.tracking_number || shippingBooking?.awb); toast.success('Resi disalin!'); }} className="text-indigo-600 p-0.5 dark:text-indigo-400"><Copy className="w-3 h-3" /></button>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Step 1: Booking */}
                      {!order?.komerce_order_no && !shippingBooking && (
                        <div className="space-y-2">
                          <p className="text-[10px] text-slate-500 flex items-center gap-1.5 dark:text-zinc-400">
                            <span className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-[10px] font-bold shrink-0 dark:text-indigo-400 dark:bg-indigo-950/40">1</span>
                            Buat booking untuk dapat nomor order
                          </p>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-[10px]">Kurir</Label>
                              <select value={courier || 'JNE'} onChange={(e) => setCourier(e.target.value)} className="w-full mt-1 px-2 py-1.5 text-xs border rounded-lg bg-white dark:bg-zinc-800 dark:border-zinc-700">
                                <option value="JNE">JNE</option><option value="SICEPAT">SiCepat</option><option value="J&T">J&T</option><option value="ANTERAJA">AnterAja</option>
                              </select>
                            </div>
                            <div>
                              <Label className="text-[10px]">Layanan</Label>
                              <select className="w-full mt-1 px-2 py-1.5 text-xs border rounded-lg bg-white dark:bg-zinc-800 dark:border-zinc-700" defaultValue="REG">
                                <option value="REG">Regular</option><option value="YES">Express</option><option value="OKE">Ekonomi</option>
                              </select>
                            </div>
                          </div>
                          <Button onClick={handleAutoBooking} disabled={bookingLoading} className="w-full h-9 bg-indigo-600 hover:bg-indigo-700 text-xs rounded-xl gap-1.5">
                            {bookingLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Package className="w-3.5 h-3.5" />}
                            Buat Booking
                          </Button>
                          {!order?.address && <p className="text-[10px] text-amber-600 dark:text-amber-400">Alamat customer belum tersedia.</p>}
                        </div>
                      )}

                      {/* Step 2: Delivery Method */}
                      {(order?.komerce_order_no || shippingBooking) && !order?.tracking_number && !shippingBooking?.awb && (
                        <div className="space-y-3">
                          <p className="text-[10px] text-slate-500 flex items-center gap-1.5 dark:text-zinc-400">
                            <span className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-[10px] font-bold shrink-0 dark:text-indigo-400 dark:bg-indigo-950/40">2</span>
                            Pilih metode pengiriman
                          </p>
                          <div className="grid grid-cols-2 gap-2">
                            <button onClick={() => setDeliveryMethod('pickup')} className={`p-2.5 rounded-lg border-2 text-left transition-all ${deliveryMethod === 'pickup' ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20' : 'border-slate-200 dark:border-zinc-700'}`}>
                              <div className="flex items-center gap-1.5 mb-0.5">
                                <Truck className={`w-3.5 h-3.5 ${deliveryMethod === 'pickup' ? 'text-purple-600' : 'text-slate-500 dark:text-zinc-400'} dark:text-purple-400`} />
                                <span className={`text-xs font-medium ${deliveryMethod === 'pickup' ? 'text-purple-700 dark:text-purple-300' : 'text-slate-600'}`}>Pickup</span>
                              </div>
                              <p className="text-[10px] text-slate-500 dark:text-zinc-400">Kurir jemput ke toko</p>
                            </button>
                            <button onClick={() => setDeliveryMethod('dropoff')} className={`p-2.5 rounded-lg border-2 text-left transition-all ${deliveryMethod === 'dropoff' ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20' : 'border-slate-200 dark:border-zinc-700'}`}>
                              <div className="flex items-center gap-1.5 mb-0.5">
                                <MapPin className={`w-3.5 h-3.5 ${deliveryMethod === 'dropoff' ? 'text-orange-600' : 'text-slate-500 dark:text-zinc-400'} dark:text-orange-400`} />
                                <span className={`text-xs font-medium ${deliveryMethod === 'dropoff' ? 'text-orange-700 dark:text-orange-300' : 'text-slate-600'}`}>Drop-off</span>
                              </div>
                              <p className="text-[10px] text-slate-500 dark:text-zinc-400">Antar ke counter</p>
                            </button>
                          </div>

                          {/* Pickup Form */}
                          {deliveryMethod === 'pickup' && (
                            <div className="space-y-2 bg-purple-50 dark:bg-purple-900/10 rounded-lg p-3">
                              <p className="text-[10px] text-purple-700 dark:text-purple-300">Kurir akan jemput ke toko. <strong>Print & tempel label sendiri.</strong></p>
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <Label className="text-[10px]">Tanggal</Label>
                                  <Input type="date" value={pickupDate} onChange={(e) => setPickupDate(e.target.value)} min={new Date().toISOString().split('T')[0]} className="mt-1 h-8 text-xs" />
                                </div>
                                <div>
                                  <Label className="text-[10px]">Jam</Label>
                                  <select value={pickupTime} onChange={(e) => setPickupTime(e.target.value)} className="w-full mt-1 px-2 py-1 text-xs border rounded-lg bg-white dark:bg-zinc-800 dark:border-zinc-700">
                                    {['09:00','10:00','11:00','12:00','13:00','14:00','15:00'].map(t => <option key={t} value={t}>{t}</option>)}
                                  </select>
                                </div>
                              </div>
                              <div>
                                <Label className="text-[10px]">Kendaraan</Label>
                                <select value={pickupVehicle} onChange={(e) => setPickupVehicle(e.target.value)} className="w-full mt-1 px-2 py-1 text-xs border rounded-lg bg-white dark:bg-zinc-800 dark:border-zinc-700">
                                  <option value="Motor">Motor</option><option value="Mobil">Mobil</option><option value="Truk">Truk</option>
                                </select>
                              </div>
                              <Button onClick={handleRequestPickup} disabled={pickupLoading} className="w-full h-9 bg-purple-600 hover:bg-purple-700 text-xs rounded-xl gap-1.5">
                                {pickupLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Truck className="w-3.5 h-3.5" />}
                                Request Pickup
                              </Button>
                            </div>
                          )}

                          {/* Drop-off Info */}
                          {deliveryMethod === 'dropoff' && (
                            <div className="space-y-2 bg-orange-50 dark:bg-orange-900/10 rounded-lg p-3">
                              <p className="text-[10px] text-orange-700 dark:text-orange-300">Antar ke counter. <strong>Label di-print di counter.</strong></p>
                              <div className="bg-white dark:bg-zinc-900 rounded-lg p-2.5 border border-orange-200 dark:border-orange-800">
                                <p className="text-[10px] text-slate-500 mb-0.5 dark:text-zinc-400">Kode booking:</p>
                                <div className="flex items-center justify-between">
                                  <span className="font-mono text-base font-bold text-orange-600 dark:text-orange-400">{order?.komerce_order_no || shippingBooking?.order_no}</span>
                                  <button onClick={() => { navigator.clipboard.writeText(order?.komerce_order_no || shippingBooking?.order_no); toast.success('Kode disalin!'); }} className="text-orange-600 p-1 dark:text-orange-400"><Copy className="w-3.5 h-3.5" /></button>
                                </div>
                              </div>
                              <Button variant="outline" className="w-full h-9 text-xs border-orange-300 text-orange-700 hover:bg-orange-100 rounded-xl gap-1.5 dark:text-orange-300 dark:bg-orange-950/40" onClick={() => {
                                const courierName = (order?.courier || courier || 'sicepat').toLowerCase();
                                let q = courierName.includes('jne') ? 'agen JNE terdekat' : courierName.includes('sicepat') ? 'agen SiCepat terdekat' : courierName.includes('j&t') || courierName.includes('jnt') ? 'agen J&T Express terdekat' : `agen ${courierName} terdekat`;
                                window.open(`https://www.google.com/maps/search/${encodeURIComponent(q)}`, '_blank');
                              }}>
                                <MapPin className="w-3.5 h-3.5" /> Cari Agen Terdekat
                              </Button>
                              <Button variant="outline" className="w-full h-9 text-xs rounded-xl gap-1.5" onClick={() => toast.success('Status diupdate. Resi akan muncul setelah diproses.')}>
                                <CheckCircle className="w-3.5 h-3.5" /> Sudah Drop-off
                              </Button>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Step 3: Print Label & Track */}
                      {(order?.tracking_number || shippingBooking?.awb) && (
                        <div className="space-y-2">
                          <p className="text-xs text-green-600 dark:text-green-400 font-medium flex items-center gap-1.5"><CheckCircle className="w-4 h-4" /> Pengiriman siap!</p>
                          <div className="flex gap-2">
                            <Button onClick={handlePrintLabel} disabled={labelLoading} variant="outline" className="flex-1 h-9 text-xs rounded-xl gap-1">
                              {labelLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                              Print Label
                            </Button>
                            <Button variant="outline" className="flex-1 h-9 text-xs rounded-xl gap-1" onClick={() => window.open(`https://cekresi.com/?noresi=${order?.tracking_number || shippingBooking?.awb}`, '_blank')}>
                              <Search className="w-3.5 h-3.5" /> Lacak
                            </Button>
                          </div>
                        </div>
                      )}

                      <div className="text-[10px] text-slate-500 dark:text-zinc-400 bg-slate-50 dark:bg-zinc-800/50 rounded-lg p-2 space-y-0.5">
                        <p><strong>Auto Booking</strong> membuat order otomatis via Komerce.</p>
                        <p><strong>Pickup</strong>: Kurir jemput (print label sendiri) · <strong>Drop-off</strong>: Antar ke counter</p>
                      </div>
                      </>}
                      {/* === END AUTO BOOKING CONTENT (HIDDEN) === */}
                    </div>
                  )}
                </div>
            </div>
            )}

            {/* ═══ PENGIRIMAN DIGITAL (produk digital saja) ═══ */}
            {!needsShipping && (
              <div className="rounded-xl border border-slate-100 dark:border-zinc-800/60 overflow-hidden" data-testid="digital-delivery-section">
                <div className="w-full flex items-center justify-between px-4 py-3 bg-white dark:bg-zinc-900">
                  <span className="flex items-center gap-2 text-xs font-semibold text-slate-600 dark:text-zinc-300 uppercase tracking-wide">
                    <Download className="w-3.5 h-3.5" /> Pengiriman Digital
                  </span>
                  {order.delivery_status && (
                    <Badge className={`text-[10px] px-1.5 py-0 h-4 ${
                      order.delivery_status === 'delivered' ? 'bg-green-100 text-green-700 dark:bg-green-950/40 dark:text-green-300'
                        : order.delivery_status === 'failed' ? 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300'
                        : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-950/40 dark:text-yellow-300'}`} data-testid="delivery-status-badge">
                      {order.delivery_status === 'delivered' ? 'Terkirim' : order.delivery_status === 'failed' ? 'Gagal' : 'Sebagian'}
                    </Badge>
                  )}
                </div>
                <div className="px-4 pb-3 bg-white dark:bg-zinc-900 space-y-2">
                  {deliveries.length === 0 ? (
                    <p className="text-xs text-slate-500 dark:text-zinc-400">
                      Belum dikirim. Produk digital terkirim otomatis saat status diubah ke "Sudah Bayar".
                    </p>
                  ) : (
                    deliveries.map((d) => (
                      <div key={d.id} className="text-xs border border-slate-100 dark:border-zinc-800 rounded-lg p-2.5" data-testid={`delivery-item-${d.id}`}>
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-medium text-slate-900 dark:text-zinc-50">{d.product_name}</p>
                          <span className="text-[10px] text-slate-500 dark:text-zinc-400">
                            {d.digital_type === 'file' ? 'File' : d.digital_type === 'license_code' ? 'Kode Lisensi' : 'Link Akses'}
                          </span>
                        </div>
                        {d.status === 'failed' ? (
                          <p className="text-[11px] text-red-600 dark:text-red-400 mt-1">
                            Gagal: {d.error === 'code_pool_empty' ? 'stok kode habis' : d.error === 'asset_missing' ? 'file belum diatur' : d.error === 'access_url_missing' ? 'link akses belum diatur' : d.error}
                          </p>
                        ) : (
                          <p className="text-[11px] text-slate-500 dark:text-zinc-400 mt-1">
                            {d.digital_type === 'file'
                              ? `Diunduh ${d.download_count || 0}x dari maks ${d.download_limit || '-'}${d.expires_at ? ` · berlaku s/d ${formatDate(d.expires_at)}` : ''}`
                              : `Terkirim ${d.sent_at ? formatDate(d.sent_at) : ''}`}
                          </p>
                        )}
                      </div>
                    ))
                  )}
                  <Button size="sm" variant="outline" onClick={handleDeliverDigital} disabled={delivering}
                    className="w-full h-8 text-xs rounded-lg gap-1" data-testid="resend-delivery-btn">
                    {delivering ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                    {deliveries.length === 0 ? 'Kirim Sekarang' : 'Kirim Ulang'}
                  </Button>
                </div>
              </div>
            )}

            {/* ═══ COLLAPSIBLE: Invoice ═══ */}
            <div className="rounded-xl border border-slate-100 dark:border-zinc-800/60 overflow-hidden">
              <button onClick={() => toggleSection('invoice')} className="w-full flex items-center justify-between px-4 py-3 bg-white dark:bg-zinc-900 hover:bg-slate-50 dark:hover:bg-zinc-800/50 transition-colors sm:pointer-events-none" data-testid="toggle-invoice-section">
                <span className="flex items-center gap-2 text-xs font-semibold text-slate-600 dark:text-zinc-300 uppercase tracking-wide">
                  <FileText className="w-3.5 h-3.5" /> Invoice
                  {invoice && <Badge className={`text-[10px] px-1.5 py-0 h-4 ml-1 ${invoice.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'} dark:text-green-300 dark:bg-green-950/40`}>{invoice.status === 'paid' ? 'Lunas' : 'Belum'}</Badge>}
                </span>
                <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform duration-200 sm:hidden ${expandedSections.invoice ? 'rotate-180' : ''} dark:text-zinc-400`} />
              </button>
              <div className={`px-4 pb-3 bg-white dark:bg-zinc-900 ${expandedSections.invoice ? '' : 'hidden sm:block'}`}>
                  {loadingInvoice ? (
                    <div className="flex items-center gap-2 text-slate-500 text-xs py-2 dark:text-zinc-400"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Memuat...</div>
                  ) : invoice ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-xs">
                        <span className="font-medium text-slate-900 dark:text-zinc-50">{invoice.invoice_number}</span>
                      </div>
                      {invoice.customer_email && <p className="text-[10px] text-slate-500 dark:text-zinc-400">Email: {invoice.customer_email}</p>}
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={handleDownloadPdf} disabled={downloadingPdf} className="flex-1 h-8 text-xs rounded-lg gap-1">
                          {downloadingPdf ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} PDF
                        </Button>
                        {invoice.customer_email && (
                          <Button size="sm" variant="outline" onClick={handleSendEmail} disabled={sendingEmail} className="flex-1 h-8 text-xs rounded-lg gap-1">
                            {sendingEmail ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />} Email
                          </Button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500 py-1 dark:text-zinc-400">Invoice belum dibuat.</p>
                  )}
                  {/* Kirim ulang invoice tidak tergantung dokumen invoice: emailnya dirakit dari data pesanan. */}
                  {order.customer_email && (
                    <div className="space-y-1 pt-2">
                      <Button size="sm" variant="outline" onClick={handleResendInvoice} disabled={resendingInvoice}
                        className="w-full h-8 text-xs rounded-lg gap-1" data-testid="resend-invoice-btn">
                        {resendingInvoice ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                        Kirim Ulang Invoice (dengan QRIS)
                      </Button>
                      {order.invoice_last_sent_at && (
                        <p className="text-[10px] text-slate-500 dark:text-zinc-500">Terakhir dikirim ulang: {formatDate(order.invoice_last_sent_at)}</p>
                      )}
                    </div>
                  )}
              </div>
            </div>

            {/* Chat + Timestamps */}
            <Button variant="outline" className="w-full h-9 gap-1.5 border-green-200 text-green-700 hover:bg-green-50 dark:border-green-800 dark:text-green-400 dark:hover:bg-green-900/20 text-xs rounded-xl dark:bg-green-950/40" onClick={() => { if (order.conversation_id) { onClose(); onNavigateChat(order.conversation_id); } else { toast.info('Chat tidak tersedia'); } }} data-testid={`chat-order-detail-${order.id}`}>
              <MessageCircle className="w-3.5 h-3.5" /> Buka Chat Customer
            </Button>

            <div className="text-[10px] text-slate-500 dark:text-zinc-500 space-y-0.5 pb-2">
              <p>Dibuat: {formatDate(order.created_at)}</p>
              {order.updated_at && <p>Update: {formatDate(order.updated_at)}</p>}
              {order.shipped_at && <p>Dikirim: {formatDate(order.shipped_at)}</p>}
            </div>

          </div>
        </div>

        {/* ═══ STICKY BOTTOM ACTION BAR ═══ */}
        <div className="sticky bottom-0 bg-white dark:bg-[#0A0A0A] border-t border-slate-200 dark:border-zinc-800 px-4 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.05)]" data-testid="order-action-bar">
          <div className="flex items-center gap-2">
            <select value={newStatus} onChange={(e) => setNewStatus(e.target.value)} className="flex-1 px-3 py-2.5 border border-slate-200 dark:border-zinc-700 rounded-xl bg-white dark:bg-zinc-900 text-slate-900 dark:text-zinc-50 text-xs font-medium" data-testid="status-select">
              <option value="Pending">Belum Bayar</option>
              <option value="Verifikasi">Verifikasi</option>
              <option value="Paid">Sudah Bayar</option>
              {needsShipping && <option value="Shipped">Dikirim</option>}
              <option value="Completed">Selesai</option>
              <option value="Cancelled">Dibatalkan</option>
            </select>
            <Button onClick={handleStatusUpdate} disabled={saving || newStatus === toSelectValue(order.status)} className="bg-indigo-600 hover:bg-indigo-700 h-10 px-4 text-xs font-semibold rounded-xl gap-1.5 shrink-0" data-testid="update-status-btn">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              Update Status
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Main Component
export default function OrdersPage() {
  const { user, token } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [channelFilter, setChannelFilter] = useState('all');
  const [productFilter, setProductFilter] = useState('all');
  const [dateRange, setDateRange] = useState({ from: undefined, to: undefined });
  const [products, setProducts] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  
  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 25,
    total: 0,
    total_pages: 1,
    has_prev: false,
    has_next: false
  });

  const headers = { Authorization: `Bearer ${token}` };

  // Channel options
  const channelOptions = [
    { value: 'all', label: 'Semua Channel', icon: Globe },
    { value: 'WhatsApp', label: 'WhatsApp', icon: MessageCircle },
    { value: 'Instagram', label: 'Instagram', icon: Instagram },
    { value: 'Messenger', label: 'Messenger', icon: Facebook },
    { value: 'Website', label: 'Website', icon: Globe }
  ];

  // Fetch products for filter dropdown
  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products/list?user_id=${user.id}`, { headers });
      setProducts(response.data.products || []);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchOrders = async (page = pagination.page) => {
    try {
      const params = new URLSearchParams({ user_id: user.id });
      params.append('page', page.toString());
      params.append('per_page', pagination.per_page.toString());
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (searchQuery) params.append('search', searchQuery);
      if (channelFilter !== 'all') params.append('channel', channelFilter);
      if (productFilter !== 'all') params.append('product_id', productFilter);
      if (dateRange.from) {
        params.append('start_date', format(dateRange.from, 'yyyy-MM-dd'));
      }
      if (dateRange.to) {
        params.append('end_date', format(dateRange.to, 'yyyy-MM-dd'));
      }

      const response = await axios.get(`${API}/orders?${params}`, { headers });
      setOrders(response.data.orders || []);
      if (response.data.pagination) {
        setPagination(response.data.pagination);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error(t('orders.loadError') || 'Gagal memuat pesanan');
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/orders/stats?user_id=${user.id}`, { headers });
      setStats(response.data.stats);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // Go to specific page
  const goToPage = (page) => {
    if (page >= 1 && page <= pagination.total_pages) {
      fetchOrders(page);
    }
  };

  useEffect(() => {
    if (user?.id) {
      Promise.all([fetchOrders(1), fetchStats(), fetchProducts()]).finally(() => setLoading(false));
    }
  }, [user]);

  useEffect(() => {
    if (user?.id) {
      fetchOrders(1); // Reset to page 1 when filters change
    }
  }, [statusFilter, searchQuery, channelFilter, productFilter, dateRange]);

  const handleStatusUpdate = async (orderId, newStatus) => {
    try {
      const res = await axios.put(`${API}/orders/${orderId}/status?user_id=${user.id}`, 
        { status: newStatus }, 
        { headers }
      );
      toast.success(t('orders.statusUpdated') || 'Status berhasil diupdate');
      if (res.data?.stock === 'restored') {
        toast.info('Stok produk dikembalikan ke katalog');
      } else if (res.data?.stock === 'deducted') {
        toast.info('Stok produk dikurangi lagi karena pesanan diaktifkan kembali');
      }
      fetchOrders();
      fetchStats();
      setShowDetail(false);
    } catch (error) {
      toast.error(t('orders.statusUpdateFailed') || 'Gagal update status');
    }
  };

  const handleTrackingUpdate = async (orderId, trackingNumber, courier) => {
    try {
      await axios.put(`${API}/orders/${orderId}/tracking?user_id=${user.id}`,
        { tracking_number: trackingNumber, courier },
        { headers }
      );
      toast.success(t('orders.trackingSaved') || 'Info pengiriman berhasil disimpan');
      fetchOrders();
      fetchStats();
      setShowDetail(false);
    } catch (error) {
      toast.error(t('orders.trackingSaveFailed') || 'Gagal menyimpan info pengiriman');
    }
  };

  const openOrderDetail = (order) => {
    setSelectedOrder(order);
    setShowDetail(true);
  };

  const hasAnyData = stats && stats.total_orders > 0;
  const hasOrders = orders.length > 0;
  const activeFilterCount = [statusFilter !== 'all', channelFilter !== 'all', productFilter !== 'all', dateRange.from].filter(Boolean).length;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0A0A0A]">
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

      <div className="lg:ml-64">
        {/* Header — compact on mobile */}
        <header className="sticky top-0 backdrop-blur-xl bg-white/85 dark:bg-[#0A0A0A]/85 border-b border-slate-200 dark:border-zinc-800/80 z-30">
          <div className="px-3 sm:px-6 py-2.5 sm:py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 sm:gap-3">
                <button
                  onClick={() => setSidebarOpen(true)}
                  className="lg:hidden p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-zinc-800 dark:bg-zinc-800"
                  data-testid="mobile-menu-btn"
                >
                  <Menu className="w-5 h-5" />
                </button>
                <h1 className="text-lg sm:text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50" style={{ fontFamily: 'Manrope' }} data-testid="page-title">
                  {t('orders.title')}
                </h1>
              </div>
              <p className="text-sm text-slate-500 hidden sm:block dark:text-zinc-400">{t('orders.subtitle')}</p>
            </div>
          </div>

          {/* Control Bar — Search + Filter icon, visible only when data exists */}
          {hasAnyData && (
            <div className="px-3 sm:px-6 pb-2.5 sm:pb-4">
              <div className="flex items-center gap-2" data-testid="control-bar">
                <div className="relative flex-1">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 dark:text-zinc-400" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={t('orders.searchOrders') || 'Cari pesanan...'}
                    className="pl-8 h-9 text-sm"
                    data-testid="search-orders-input"
                  />
                </div>
                <button
                  onClick={() => setFilterDrawerOpen(true)}
                  className={`relative p-2 h-9 w-9 flex items-center justify-center rounded-lg border transition-colors sm:hidden ${
                    activeFilterCount > 0
                      ? 'border-blue-500 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600'
                      : 'border-slate-200 dark:border-zinc-800 hover:bg-slate-100 dark:hover:bg-zinc-800 text-slate-600 dark:text-zinc-300'
                  }`}
                  data-testid="filter-btn"
                >
                  <SlidersHorizontal className="w-4 h-4" />
                  {activeFilterCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-indigo-500 text-white rounded-full text-[10px] flex items-center justify-center font-medium">{activeFilterCount}</span>
                  )}
                </button>
              </div>

              {/* Active filter chips on mobile */}
              {activeFilterCount > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2 sm:hidden" data-testid="active-filters-mobile">
                  {statusFilter !== 'all' && (
                    <Badge variant="secondary" className="bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 text-[11px] px-2 py-0.5 flex items-center gap-1">
                      {STATUS_CONFIG[statusFilter]?.label || statusFilter}
                      <button onClick={() => setStatusFilter('all')}><X className="w-2.5 h-2.5" /></button>
                    </Badge>
                  )}
                  {channelFilter !== 'all' && (
                    <Badge variant="secondary" className="bg-indigo-50 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300 text-[11px] px-2 py-0.5 flex items-center gap-1">
                      {channelFilter}
                      <button onClick={() => setChannelFilter('all')}><X className="w-2.5 h-2.5" /></button>
                    </Badge>
                  )}
                  {productFilter !== 'all' && (
                    <Badge variant="secondary" className="bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300 text-[11px] px-2 py-0.5 flex items-center gap-1 max-w-[120px]">
                      <span className="truncate">{products.find(p => p.id === productFilter)?.name || 'Produk'}</span>
                      <button onClick={() => setProductFilter('all')}><X className="w-2.5 h-2.5 shrink-0" /></button>
                    </Badge>
                  )}
                  {dateRange.from && (
                    <Badge variant="secondary" className="bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 text-[11px] px-2 py-0.5 flex items-center gap-1">
                      {format(dateRange.from, "dd/MM")} - {dateRange.to ? format(dateRange.to, "dd/MM") : '...'}
                      <button onClick={() => setDateRange({ from: undefined, to: undefined })}><X className="w-2.5 h-2.5" /></button>
                    </Badge>
                  )}
                </div>
              )}

              {/* Desktop: inline filters */}
              <div className="hidden sm:flex items-center gap-2 mt-2 flex-wrap">
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-1.5 rounded-lg border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-sm" data-testid="status-filter-dropdown">
                  <option value="all">{t('orders.allStatus') || 'Semua Status'}</option>
                  <option value="Pending">Belum Bayar</option>
                  <option value="Paid">Sudah Bayar</option>
                  <option value="Shipped">Dikirim</option>
                  <option value="Completed">Selesai</option>
                  <option value="Cancelled">Dibatalkan</option>
                </select>
                <select value={channelFilter} onChange={(e) => setChannelFilter(e.target.value)}
                  className="px-3 py-1.5 rounded-lg border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-sm" data-testid="channel-filter-dropdown">
                  {channelOptions.map(opt => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
                </select>
                <select value={productFilter} onChange={(e) => setProductFilter(e.target.value)}
                  className="px-3 py-1.5 rounded-lg border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-sm" data-testid="product-filter-dropdown">
                  <option value="all">Semua Produk</option>
                  {products.map(p => (<option key={p.id} value={p.id}>{p.name}</option>))}
                </select>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-8 text-sm" data-testid="date-range-picker">
                      <CalendarIcon className="w-3.5 h-3.5 mr-1.5 text-slate-500 dark:text-zinc-400" />
                      {dateRange.from ? (
                        dateRange.to
                          ? <>{format(dateRange.from, "dd MMM", { locale: idLocale })} - {format(dateRange.to, "dd MMM", { locale: idLocale })}</>
                          : format(dateRange.from, "dd MMM yyyy", { locale: idLocale })
                      ) : 'Pilih Tanggal'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-white dark:bg-zinc-900" align="end">
                    <Calendar initialFocus mode="range" defaultMonth={dateRange.from} selected={dateRange} onSelect={setDateRange} numberOfMonths={1} locale={idLocale} />
                    <div className="p-3 border-t border-slate-200 dark:border-zinc-800">
                      <Button variant="ghost" size="sm" onClick={() => setDateRange({ from: undefined, to: undefined })} className="w-full">Reset</Button>
                    </div>
                  </PopoverContent>
                </Popover>
                <Button variant="ghost" size="sm" onClick={() => { fetchOrders(); fetchStats(); }} className="text-slate-500 h-8 dark:text-zinc-400" data-testid="refresh-btn">
                  <RefreshCw className="w-3.5 h-3.5 mr-1" />Refresh
                </Button>
              </div>
            </div>
          )}
        </header>

        {/* Main Content */}
        <main className="p-3 sm:p-6">
          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-16 sm:py-24">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          )}
          {/* Empty State — shown immediately when no data */}
          {!hasAnyData && !loading && (
            <div className="flex flex-col items-center justify-center py-16 sm:py-24 px-4" data-testid="empty-state">
              <div className="w-16 h-16 rounded-full bg-slate-100 dark:bg-zinc-900 flex items-center justify-center mb-4">
                <Package className="w-8 h-8 text-slate-500 dark:text-zinc-400" />
              </div>
              <h3 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-zinc-50 mb-1 text-center">
                Belum ada pesanan
              </h3>
              <p className="text-sm text-slate-500 dark:text-zinc-400 text-center max-w-xs mb-6">
                Pesanan dari chat AI akan muncul otomatis di sini
              </p>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button onClick={() => navigate('/inbox')} className="bg-indigo-600 hover:bg-indigo-700" data-testid="cta-inbox">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Mulai Terima Pesanan
                </Button>
                <Button variant="outline" onClick={() => navigate('/channels')} data-testid="cta-channels">
                  <Inbox className="w-4 h-4 mr-2" />
                  Hubungkan Channel
                </Button>
              </div>
              {/* Education steps */}
              <div className="mt-8 flex flex-col gap-2 text-sm text-slate-500 dark:text-zinc-500">
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-slate-200 dark:bg-zinc-800 text-[11px] flex items-center justify-center font-medium text-slate-500 dark:text-zinc-400">1</span>
                  Hubungkan channel (WhatsApp/IG)
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-slate-200 dark:bg-zinc-800 text-[11px] flex items-center justify-center font-medium text-slate-500 dark:text-zinc-400">2</span>
                  Aktifkan AI auto-reply
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-slate-200 dark:bg-zinc-800 text-[11px] flex items-center justify-center font-medium text-slate-500 dark:text-zinc-400">3</span>
                  Pesanan masuk otomatis
                </div>
              </div>
            </div>
          )}

          {/* Orders List — shown when data exists */}
          {hasAnyData && (
            <>
              {/* Stats — desktop only, above content */}
              {stats && (
                <div className="hidden sm:grid grid-cols-2 md:grid-cols-4 gap-4 mb-4" data-testid="stats-section">
                  <div className="bg-white dark:bg-zinc-900 rounded-xl p-4 border border-slate-200 dark:border-zinc-800">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500 text-sm dark:text-zinc-400">{t('orders.totalOrders') || 'Total Pesanan'}</span>
                      <ShoppingBag className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <p className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50 mt-1">{stats.total_orders}</p>
                  </div>
                  <div className="bg-white dark:bg-zinc-900 rounded-xl p-4 border border-orange-200 dark:border-orange-700">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500 text-sm dark:text-zinc-400">Verifikasi</span>
                      <AlertCircle className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                    </div>
                    <p className="text-2xl font-bold text-orange-600 dark:text-orange-400 mt-1">{stats.by_status?.verifikasi || 0}</p>
                  </div>
                  <div className="bg-white dark:bg-zinc-900 rounded-xl p-4 border border-slate-200 dark:border-zinc-800">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500 text-sm dark:text-zinc-400">{t('orders.receivables') || 'Piutang'}</span>
                      <CreditCard className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                    </div>
                    <p className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50 mt-1">{formatCurrency(stats.pending_revenue || 0)}</p>
                  </div>
                  <div className="bg-white dark:bg-zinc-900 rounded-xl p-4 border border-slate-200 dark:border-zinc-800">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500 text-sm dark:text-zinc-400">{t('orders.revenue') || 'Revenue'}</span>
                      <TrendingUp className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                    </div>
                    <p className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50 mt-1">{formatCurrency(stats.paid_revenue || stats.today_revenue || 0)}</p>
                  </div>
                </div>
              )}

              {/* Mobile Order List — flat, no wrapper card */}
              <div className="sm:hidden" data-testid="orders-mobile">
                {hasOrders ? (
                  <div className="divide-y divide-slate-200 dark:divide-zinc-800">
                    {orders.map((order) => {
                      const statusConfig = STATUS_CONFIG[order.status] || STATUS_CONFIG['Pending'];
                      const StatusIcon = statusConfig.icon || Clock;
                      const channelLower = (order.channel || '').toLowerCase();
                      const channel = CHANNEL_CONFIG[channelLower] || CHANNEL_CONFIG['website'];
                      const ChannelIcon = channel.icon;
                      const productName = order.items?.[0]?.product || order.product || '-';
                      const totalQty = order.items?.reduce((sum, item) => sum + (item.quantity || 1), 0) || order.quantity || 1;
                      const extraItems = (order.items?.length || 1) - 1;

                      return (
                        <div
                          key={order.id}
                          className="flex items-center gap-2.5 py-3 px-1 active:bg-slate-100 dark:active:bg-slate-800 transition-colors cursor-pointer dark:bg-zinc-800"
                          onClick={() => openOrderDetail(order)}
                          data-testid={`order-card-${order.id}`}
                        >
                          <div className="relative flex-shrink-0">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 dark:from-slate-600 dark:to-slate-700 flex items-center justify-center text-slate-600 dark:text-zinc-300 text-sm font-semibold">
                              {(order.customer_name || 'C').charAt(0).toUpperCase()}
                            </div>
                            <div className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full ${channel.bg} flex items-center justify-center ring-[1.5px] ring-white dark:ring-slate-900`}>
                              <ChannelIcon className={`w-2.5 h-2.5 ${channel.color}`} />
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 mb-0.5">
                              <p className="font-medium text-sm text-slate-900 dark:text-zinc-50 truncate">{order.customer_name || 'Customer'}</p>
                              <span className="font-mono text-[10px] text-slate-500 flex-shrink-0 dark:text-zinc-400">#{order.id?.slice(-6).toUpperCase()}</span>
                            </div>
                            <p className="text-xs text-slate-500 dark:text-zinc-400 truncate mb-1">
                              {productName} x{totalQty}
                              {extraItems > 0 && <span className="text-blue-500 ml-1">+{extraItems}</span>}
                            </p>
                            <div className="flex items-center gap-2">
                              <Badge className={`${statusConfig.color} text-[10px] font-medium px-1.5 py-0.5 leading-none`}>
                                <StatusIcon className="w-2.5 h-2.5 mr-0.5" />{statusConfig.label || order.status}
                              </Badge>
                              {order.cancel_requested_at && !['Cancelled', 'Dibatalkan'].includes(order.status) && (
                                <Badge
                                  className="bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-950/60 dark:text-amber-300 dark:border-amber-800/60 text-[10px] font-medium px-1.5 py-0.5 leading-none"
                                  data-testid={`cancel-requested-badge-mobile-${order.id}`}
                                >
                                  Minta batal
                                </Badge>
                              )}
                              <span className="text-[10px] text-slate-500 dark:text-zinc-400">{formatDate(order.created_at).split(',')[0]}</span>
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                            <p className="text-sm font-bold text-indigo-600 dark:text-indigo-400">{formatCurrency(order.total)}</p>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (order.conversation_id) navigate(`/inbox/${order.conversation_id}`);
                                else toast.info('Chat tidak tersedia');
                              }}
                              className="p-1.5 rounded-lg bg-green-50 hover:bg-green-100 dark:bg-green-900/30 dark:hover:bg-green-900/50 text-green-600 dark:text-green-400 transition-colors"
                              data-testid={`chat-order-mobile-${order.id}`}
                            >
                              <MessageCircle className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12 text-sm text-slate-500 dark:text-zinc-400" data-testid="no-results">
                    Tidak ada pesanan yang cocok
                  </div>
                )}
              </div>

              {/* Desktop Table */}
              <div className="hidden sm:block bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="orders-table">
                    <thead className="bg-slate-50 dark:bg-zinc-800/50 sticky top-0 z-10">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider dark:text-zinc-400">Order</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider dark:text-zinc-400">Customer</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider dark:text-zinc-400">Produk</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider dark:text-zinc-400">Total</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider dark:text-zinc-400">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider dark:text-zinc-400">Tanggal</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider dark:text-zinc-400">Aksi</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 dark:divide-zinc-800">
                      {orders.map((order) => {
                        const StatusIcon = STATUS_CONFIG[order.status]?.icon || Clock;
                        return (
                          <tr key={order.id} className="hover:bg-slate-50 dark:hover:bg-zinc-800/50 transition-colors dark:bg-zinc-900" data-testid={`order-row-${order.id}`}>
                            <td className="px-4 py-4">
                              <p className="font-mono text-sm font-medium text-slate-900 dark:text-zinc-50">{order.id?.slice(0, 15)}...</p>
                              <p className="text-xs text-slate-500 dark:text-zinc-400">{order.channel || 'Website'}</p>
                            </td>
                            <td className="px-4 py-4">
                              <p className="font-medium text-slate-900 dark:text-zinc-50">{order.customer_name || '-'}</p>
                              <p className="text-xs text-slate-500 dark:text-zinc-400">{order.customer_phone || '-'}</p>
                            </td>
                            <td className="px-4 py-4">
                              {order.items && order.items.length > 1 ? (
                                <>
                                  <p className="text-slate-900 dark:text-zinc-50">{order.items[0]?.product || '-'} <span className="text-xs ml-1 text-indigo-600 font-medium dark:text-indigo-400">+{order.items.length - 1} lainnya</span></p>
                                  <p className="text-xs text-slate-500 dark:text-zinc-400">{order.items.map(i => `${i.variant || i.product || ''} x${i.quantity || 1}`).filter(s => s.trim()).join(', ')}</p>
                                </>
                              ) : (
                                <>
                                  <p className="text-slate-900 dark:text-zinc-50">{order.items?.[0]?.product || order.product || '-'}</p>
                                  <p className="text-xs text-slate-500 dark:text-zinc-400">{(order.items?.[0]?.variant || order.variant) && `${order.items?.[0]?.variant || order.variant} · `}Qty: {order.items?.[0]?.quantity || order.quantity || 1}</p>
                                </>
                              )}
                            </td>
                            <td className="px-4 py-4"><p className="font-semibold text-indigo-600 dark:text-indigo-400">{formatCurrency(order.total)}</p></td>
                            <td className="px-4 py-4">
                              <Badge className={`${STATUS_CONFIG[order.status]?.color} text-xs`}>
                                <StatusIcon className="w-3 h-3 mr-1" />{STATUS_CONFIG[order.status]?.label || order.status}
                              </Badge>
                              {order.cancel_requested_at && !['Cancelled', 'Dibatalkan'].includes(order.status) && (
                                <Badge
                                  className="mt-1 block w-fit bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-950/60 dark:text-amber-300 dark:border-amber-800/60 text-xs"
                                  data-testid={`cancel-requested-badge-${order.id}`}
                                >
                                  Minta batal
                                </Badge>
                              )}
                            </td>
                            <td className="px-4 py-4 text-sm text-slate-500 dark:text-zinc-400">{formatDate(order.created_at)}</td>
                            <td className="px-4 py-4">
                              <Button size="sm" variant="outline" onClick={() => openOrderDetail(order)} data-testid={`view-order-${order.id}`}>
                                <Eye className="w-4 h-4 mr-1" />Detail
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                      {orders.length === 0 && (
                        <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-500 dark:text-zinc-400">Tidak ada pesanan yang cocok</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination */}
              <div className="mt-3 sm:mt-4">
                <Pagination pagination={pagination} onPageChange={goToPage} itemLabel="pesanan" />
              </div>
            </>
          )}
        </main>
      </div>

      {/* Filter Drawer (mobile bottom sheet) */}
      <Drawer open={filterDrawerOpen} onOpenChange={setFilterDrawerOpen}>
        <DrawerContent className="bg-white dark:bg-zinc-900">
          <DrawerHeader className="pb-2">
            <DrawerTitle className="text-base font-semibold text-slate-900 dark:text-zinc-50 flex items-center gap-2">
              <SlidersHorizontal className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
              Filter Pesanan
            </DrawerTitle>
          </DrawerHeader>
          <div className="px-4 pb-6 space-y-4 max-h-[70vh] overflow-y-auto" data-testid="filter-drawer-content">
            {/* Status Filter */}
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5 block dark:text-zinc-400">Status</label>
              <div className="space-y-0.5">
                {[
                  { value: 'all', label: 'Semua Status' },
                  { value: 'Pending', label: 'Belum Bayar' },
                  { value: 'Paid', label: 'Sudah Bayar' },
                  { value: 'Shipped', label: 'Dikirim' },
                  { value: 'Completed', label: 'Selesai' },
                  { value: 'Cancelled', label: 'Dibatalkan' },
                ].map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setStatusFilter(opt.value)}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                      statusFilter === opt.value ? 'bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 font-medium' : 'text-slate-700 dark:text-zinc-300 hover:bg-slate-100 dark:hover:bg-zinc-800'
                    }`}
                    data-testid={`filter-status-${opt.value}`}
                  >
                    <span>{opt.label}</span>
                    {statusFilter === opt.value && <Check className="w-4 h-4" />}
                  </button>
                ))}
              </div>
            </div>

            {/* Channel Filter */}
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5 block dark:text-zinc-400">Channel</label>
              <div className="space-y-0.5">
                {channelOptions.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setChannelFilter(opt.value)}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                      channelFilter === opt.value ? 'bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 font-medium' : 'text-slate-700 dark:text-zinc-300 hover:bg-slate-100 dark:hover:bg-zinc-800'
                    }`}
                    data-testid={`filter-channel-${opt.value}`}
                  >
                    <span>{opt.label}</span>
                    {channelFilter === opt.value && <Check className="w-4 h-4" />}
                  </button>
                ))}
              </div>
            </div>

            {/* Product Filter */}
            {products.length > 0 && (
              <div>
                <label className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5 block dark:text-zinc-400">Produk</label>
                <div className="space-y-0.5">
                  <button onClick={() => setProductFilter('all')}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${productFilter === 'all' ? 'bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 font-medium' : 'text-slate-700 dark:text-zinc-300 hover:bg-slate-100 dark:hover:bg-zinc-800'}`}>
                    <span>Semua Produk</span>
                    {productFilter === 'all' && <Check className="w-4 h-4" />}
                  </button>
                  {products.map(p => (
                    <button key={p.id} onClick={() => setProductFilter(p.id)}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${productFilter === p.id ? 'bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 font-medium' : 'text-slate-700 dark:text-zinc-300 hover:bg-slate-100 dark:hover:bg-zinc-800'}`}>
                      <span className="truncate">{p.name}</span>
                      {productFilter === p.id && <Check className="w-4 h-4 shrink-0" />}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Date Range */}
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5 block dark:text-zinc-400">Tanggal</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left text-sm" data-testid="date-range-picker-mobile">
                    <CalendarIcon className="w-4 h-4 mr-2 text-slate-500 dark:text-zinc-400" />
                    {dateRange.from ? (
                      dateRange.to
                        ? <>{format(dateRange.from, "dd MMM", { locale: idLocale })} - {format(dateRange.to, "dd MMM yyyy", { locale: idLocale })}</>
                        : format(dateRange.from, "dd MMM yyyy", { locale: idLocale })
                    ) : 'Pilih Tanggal'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-white dark:bg-zinc-900" align="start">
                  <Calendar initialFocus mode="range" defaultMonth={dateRange.from} selected={dateRange} onSelect={setDateRange} numberOfMonths={1} locale={idLocale} />
                  <div className="p-3 border-t border-slate-200 dark:border-zinc-800">
                    <Button variant="ghost" size="sm" onClick={() => setDateRange({ from: undefined, to: undefined })} className="w-full">Reset</Button>
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-2 border-t border-slate-200 dark:border-zinc-800">
              {activeFilterCount > 0 && (
                <Button variant="ghost" size="sm" onClick={() => { setStatusFilter('all'); setChannelFilter('all'); setProductFilter('all'); setDateRange({ from: undefined, to: undefined }); }} className="flex-1 text-slate-500 dark:text-zinc-400">
                  <X className="w-4 h-4 mr-1" />Reset
                </Button>
              )}
              <Button size="sm" onClick={() => { fetchOrders(); fetchStats(); setFilterDrawerOpen(false); toast.success('Data diperbarui'); }} className="flex-1 bg-indigo-600 hover:bg-indigo-700">
                <RefreshCw className="w-4 h-4 mr-1" />Refresh
              </Button>
            </div>
          </div>
        </DrawerContent>
      </Drawer>

      {/* Order Detail Modal */}
      <OrderDetailModal
        order={selectedOrder}
        isOpen={showDetail}
        onClose={() => setShowDetail(false)}
        onStatusUpdate={handleStatusUpdate}
        onTrackingUpdate={handleTrackingUpdate}
        onNavigateChat={(conversationId) => navigate(`/inbox/${conversationId}`)}
      />
    </div>
  );
}
